'use client';

import Link from 'next/link';
import { useState } from 'react';

type Ep = { id: number; number: number; title: string };

export default function EpisodeGrid({
  slug,
  episodes,
  watchedNumbers,
}: {
  slug: string;
  episodes: Ep[];
  watchedNumbers: Set<number>;
}) {
  const [watched, setWatched] = useState<Set<number>>(watchedNumbers);
  const [pending, setPending] = useState<Set<number>>(new Set());
  const [collapsed, setCollapsed] = useState(true);

  if (!episodes.length) {
    return <p className="text-zinc-500 text-sm">No episodes released yet.</p>;
  }

  const shown = collapsed ? episodes.slice(-12) : episodes;
  const hiddenCount = episodes.length - shown.length;

  const toggleWatched = async (e: React.MouseEvent, ep: Ep) => {
    e.preventDefault();
    const next = new Set(watched);
    if (next.has(ep.number)) next.delete(ep.number);
    else next.add(ep.number);
    setWatched(next);
    setPending((p) => new Set(p).add(ep.number));
    try {
      await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ episodeId: ep.id, watched: next.has(ep.number) }),
      });
    } finally {
      setPending((p) => {
        const q = new Set(p);
        q.delete(ep.number);
        return q;
      });
    }
  };

  return (
    <div>
      <div className="grid grid-cols-4 xs:grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2">
        {shown.map((ep) => {
          const isWatched = watched.has(ep.number);
          return (
            <div key={ep.number} className="relative">
              <Link
                href={`/watch/${slug}/${ep.number}`}
                className={`flex items-center justify-center aspect-square rounded-lg text-sm font-semibold border transition ${
                  isWatched
                    ? 'bg-emerald-600/15 text-emerald-300 border-emerald-700/40'
                    : 'bg-zinc-900 text-zinc-200 border-zinc-800 hover:border-violet-500'
                }`}
              >
                {ep.number}
              </Link>
              <button
                onClick={(e) => toggleWatched(e, ep)}
                title={isWatched ? 'Mark unwatched' : 'Mark watched'}
                className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] border ${
                  isWatched
                    ? 'bg-emerald-600 text-white border-emerald-400'
                    : 'bg-zinc-700 text-zinc-400 border-zinc-600 hover:bg-zinc-600'
                } ${pending.has(ep.number) ? 'opacity-50' : ''}`}
              >
                ✓
              </button>
            </div>
          );
        })}
      </div>
      {hiddenCount > 0 && (
        <button
          onClick={() => setCollapsed(false)}
          className="mt-3 text-sm text-violet-400 hover:text-violet-300"
        >
          Show all {episodes.length} episodes ({hiddenCount} more) ↓
        </button>
      )}
      <p className="mt-3 text-xs text-zinc-600">
        ✓ = watched · click a number to watch · click the ✓ badge to toggle
      </p>
    </div>
  );
}
